import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { standardRequest } from 'shared/utils/request';
import serializeParams from 'shared/utils/paramsSerializer';
import { useCompanyInfoContext } from 'shared/components/company_info/company_info_context';
import {
  type CompanyProfile,
  type CompanyProfilesResponse,
} from 'shared/components/company_info/types';

// One cache entry per employer name, shared across rows. `null` is a cached
// negative match, distinct from "not fetched yet" (undefined).
const profileKey = (name: string) => ['companyProfile', name];

const requestProfiles = async (path: string, names: string[]) => {
  const { data } = await standardRequest.get(
    `${path}?${serializeParams({ names })}`
  );
  const companies: CompanyProfilesResponse['companies'] = data?.companies ?? {};
  return companies;
};

/**
 * Warms the cache for a whole table row's employers in one request. Called on row
 * mouse-enter so the lookup is already resolved by the time the pointer reaches a
 * company name and the card's open delay elapses.
 */
export const usePrefetchCompanyProfiles = () => {
  const queryClient = useQueryClient();
  const { canViewCompanyInfo, companyProfilesPath } = useCompanyInfoContext();

  return useCallback(
    async (names: string[]) => {
      if (!canViewCompanyInfo) {
        return;
      }

      const missing = [...new Set(names)].filter((name) => {
        const state = queryClient.getQueryState(profileKey(name));
        return (
          !state ||
          (state.data === undefined && state.fetchStatus !== 'fetching')
        );
      });
      if (missing.length === 0) {
        return;
      }

      const batch = requestProfiles(companyProfilesPath, missing);

      // Per-name keys resolving from one shared request let a card opening
      // mid-flight join the batch instead of issuing its own lookup.
      await Promise.all(
        missing.map((name) =>
          queryClient.prefetchQuery({
            queryKey: profileKey(name),
            queryFn: () => batch.then((companies) => companies[name] ?? null),
            staleTime: Infinity,
          })
        )
      );
    },
    [canViewCompanyInfo, companyProfilesPath, queryClient]
  );
};

/**
 * Reads one employer's profile. Normally resolves straight from the cache the row
 * prefetch seeded; the single-name request is the fallback for reaching a company
 * without hovering its row first, such as keyboard focus.
 */
export const useCompanyProfile = (name: string, enabled: boolean) => {
  const { canViewCompanyInfo, companyProfilesPath } = useCompanyInfoContext();

  const { data, isLoading } = useQuery<CompanyProfile | null>({
    queryKey: profileKey(name),
    queryFn: async () => {
      const companies = await requestProfiles(companyProfilesPath, [name]);
      return companies[name] ?? null;
    },
    enabled: enabled && canViewCompanyInfo && !!name,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return { profile: data ?? null, isLoading };
};
